/**
 * Profiles and workout progress, kept in this browser's localStorage.
 *
 * The layout mirrors the Android app's DataStore so the two stay conceptually
 * the same: a profile is {id, name}, and a block is identified by the key
 * "plan/week/day/block" in both clients.
 *
 * Nothing syncs. The API is read-only, so a profile here and a profile with the
 * same name on the phone are two independent records; clearing site data clears
 * the history.
 */

const PROFILES_KEY = "enchinfit.profiles.v1";
const PROGRESS_PREFIX = "enchinfit.progress.v1.";

/** A block nobody has touched yet. */
export const EMPTY_ENTRY = {done: false, ticks: []};

const readJson = (key, fallback) => {
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch (error) {
        // Private mode, disabled storage, or a corrupted value: carry on without
        // history rather than taking the whole app down.
        console.warn("EnchinFit: no se pudo leer " + key, error);
        return fallback;
    }
};

const writeJson = (key, value) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
    } catch (error) {
        console.warn("EnchinFit: no se pudo guardar " + key, error);
        return false;
    }
};

/**
 * crypto.randomUUID() only exists in a secure context, and this app is normally
 * served over plain HTTP on the LAN, so it is not available here.
 */
export const newId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
        try {
            return window.crypto.randomUUID();
        } catch (error) {
            // fall through
        }
    }
    return "p" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
};

/** Same shape as the mobile app's ProgressStore.blockKey. */
export const blockKey = (plan, week, day, block) => `${plan}/${week}/${day}/${block}`;

// ------------------------------------------------------------------ profiles

export const loadProfiles = () => {
    const state = readJson(PROFILES_KEY, null);
    if (!state || !Array.isArray(state.profiles)) return {profiles: [], activeId: null};

    const profiles = state.profiles.filter((profile) => profile && profile.id && profile.name);
    const activeId = profiles.some((profile) => profile.id === state.activeId)
        ? state.activeId
        : (profiles.length > 0 ? profiles[0].id : null);
    return {profiles, activeId};
};

export const saveProfiles = (state) => {
    writeJson(PROFILES_KEY, state);
    return state;
};

// ------------------------------------------------------------------ progress

export const loadProgress = (profileId) => (
    profileId ? readJson(PROGRESS_PREFIX + profileId, {}) : {}
);

export const saveProgress = (profileId, progress) => {
    if (profileId) writeJson(PROGRESS_PREFIX + profileId, progress);
};

export const dropProgress = (profileId) => {
    try {
        window.localStorage.removeItem(PROGRESS_PREFIX + profileId);
    } catch (error) {
        console.warn("EnchinFit: no se pudo borrar el progreso", error);
    }
};

export const entryFor = (progress, key) => progress[key] || EMPTY_ENTRY;

/**
 * How much of a block is ticked off.
 *
 * Exercises sharing a `number` are alternatives (gym vs. home) -- you do one of
 * them, not both -- so a step counts as done when *any* of its rows is ticked,
 * and the total is the number of distinct steps rather than of rows.
 */
export const blockTally = (exercises, entry) => {
    const ticks = entry.ticks || [];
    const numbers = [];
    exercises.forEach((exercise) => {
        if (!numbers.includes(exercise.number)) numbers.push(exercise.number);
    });
    const done = numbers.filter((number) => exercises.some(
        (exercise) => exercise.number === number && ticks.includes(exercise.a_id)
    )).length;
    return {done, total: numbers.length};
};
